import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { formatDate } from '@utils/format';
import { useGetMyAdherences } from '@services/useGetMyAdherences';
import { AdherenceState } from '@types';
import { AdherenceStateBadge } from '@components/molecules/badges/PromotionStateBadge';

export const MyAdherences: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { t } = useTranslation();

    const translations = {
        title: t('my-adherence-component.title'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MyAccountContainer title={translations.title}>
            <div className="pt-4">
                <AdherencesTable />
            </div>
        </MyAccountContainer>
    );
};

const AdherencesTable: FC = () => {
    const { request: requestMyAdherences } = useGetMyAdherences();
    const { myAdherences } = useData();
    const { t } = useTranslation();

    const translations = {
        promoCode: t('my-adherence-component.table.promo-code'),
        store: t('my-adherence-component.table.store'),
        discount: t('my-adherence-component.table.discount'),
        value: t('my-adherence-component.table.value'),
        startDate: t('my-adherence-component.table.start-date'),
        endDate: t('my-adherence-component.table.end-date'),
        state: t('my-adherence-component.table.state'),
        adherenceDate: t('my-adherence-component.table.adherence_date'),
        actions: t('my-adherence-component.table.actions'),
        deletePopupTitle: t('my-adherence-component.delete-popup.title'),
        deletePopupDescription: t('my-adherence-component.delete-popup.description'),
        deletePopupWarning: t('my-adherence-component.delete-popup.warning'),
        successfullyDeleted: t('my-adherence-component.delete-success'),
    };

    // call services
    useEffect(() => {
        requestMyAdherences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="management-table">
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: 'text-left', text: translations.promoCode },
                        { classes: 'text-left', hideOnMobile: true, text: translations.store },
                        { classes: 'text-center', text: translations.discount },
                        { classes: 'text-center', hideOnMobile: true, text: translations.startDate },
                        { classes: 'text-center', hideOnMobile: true, text: translations.endDate },
                        { classes: 'text-center', text: translations.state },
                        { classes: 'text-center', text: translations.adherenceDate },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={(myAdherences?.data || []).map((item) => {
                        return [
                            {
                                classes: 'text-left text-muted',
                                content: <Typography fontSize="14">{item.promo_code}</Typography>,
                            },
                            {
                                classes: 'text-left',
                                content: <Typography fontSize="14">{item.store}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{item.discount}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatDate(item.start_date ?? null)}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatDate(item.end_date ?? null)}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: (<AdherenceStateBadge state={AdherenceState[item.status as keyof typeof AdherenceState]} />),
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatDate(item.adherence_date ?? null)}</Typography>,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={[
                                            <Anchor
                                                key="option-1"
                                                href={SITE_URLS.myAdherenceDetail.replace(':uuid', `${item.uuid}`)}
                                                mode={AnchorMode.none}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-eye" />
                                                </Typography>
                                            </Anchor>,
                                        ]}
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>
        </div>
    );
};
